/**
 * The customer record, as the domain rules need it.
 *
 * Ported from the reference implementation's `Outstanding`, with two changes
 * the commercial product requires:
 *
 *  - ownership is a member id, not a text code. In the reference app a
 *    teammate's identity *was* the code their spreadsheet used, which is
 *    globally unique and immutable — two companies both employing a person
 *    coded "AK" would collide. The code survives here as `importedOwnerCode`,
 *    which is what the sheet said, so an unrecognised code is visible instead
 *    of silently dropped.
 *  - identity is a uuid plus the workspace's own `sourceKey`, not the
 *    company name.
 *
 * Everything else is the proven shape. The ageing windows are fixed product-
 * wide (see docs/saas-launch/15, D3).
 */
import type { MemberId } from './tenant.js';
import type { ContactPerson } from './contacts.js';

export type BalanceType = 'Dr' | 'Cr';

export type AgeingBucket = '1-45' | '46-90' | '91-135' | '>135';

export const AGEING_BUCKETS: readonly AgeingBucket[] = ['1-45', '46-90', '91-135', '>135'];

/**
 * An account's ageing in its company's own windows (docs/tally/18 §1.3),
 * kept inside `ageing` beside the four standard bands every rule reads, for an
 * account whose source is TallyPrime. Open debit bills per window, as
 * TallyPrime's Age-wise report shows them; what is in no window is said
 * beside them: windows + notYetDue + notAged − credits = the balance (Dr +).
 * A sheet brings its own four columns and has none.
 */
export interface AgeingWindows {
    basis: 'bill_date' | 'due_date';
    /** The windows' upper bounds in days, strictly increasing, as the company set them; the last window is open-ended. */
    boundaries: number[];
    /** Open debit bills per window, in rupees: one more than the bounds. */
    amounts: number[];
    /** By due date: open debit bills not yet due. */
    notYetDue: number;
    /** Owed in no window: money in no bill, a ledger kept without bills, a bill left out for having no due date. */
    notAged: number;
    /** Held for the customer against no bill: credit notes and advances as new references, money On Account. */
    credits: number;
}

/** The four standard bands alone. */
export type AgeingBands = Record<AgeingBucket, number>;
export type AgeingAmounts = AgeingBands & { windows?: AgeingWindows };
export type AgeingTypes = Partial<Record<AgeingBucket, BalanceType>>;

export const emptyAgeing = (): AgeingAmounts => ({ '1-45': 0, '46-90': 0, '91-135': 0, '>135': 0 });

/**
 * How a customer pays, in the three grades a collections team actually uses.
 *
 *   Good — pays to terms.
 *   Late — pays, but late; worth chasing normally. However old the money is,
 *          a customer who is still paying is late, not a defaulter.
 *   Bad  — a defaulter. This is the list that goes to recovery.
 *
 * `Bad` is **declared, never calculated.** Whether somebody has stopped paying
 * is a judgement about the customer, and ageing cannot make it: money sitting
 * past 135 days may be a dispute, a retention, or an invoice nobody chased.
 * Derived from ageing it called the majority of owing accounts defaulters,
 * which is both untrue and useless — a recovery list of four hundred names is
 * not a recovery list.
 */
export type PaymentRank = 'Good' | 'Late' | 'Bad';

export const PAYMENT_RANK_LABELS: Record<PaymentRank, string> = {
    Good: 'Good',
    // Stored as `Late`, called "Slow payer" (decided 23 Oct 2026; the screens' words are `RANK_WORDS` in semantics.ts).
    Late: 'Slow payer',
    Bad: 'Bad debt',
};

/**
 * What ranking an account Bad means, said wherever the product explains it.
 *
 * "Bad debt" is a collections decision: the account leaves Today's queue. It
 * is not an accounting write-off — the balance, the ageing and the sheet are
 * untouched, the amount stays in Outstanding, and the rank can be set back —
 * so the product never calls it "written off".
 */
export const BAD_DEBT_MEANING =
    'Operational classification only. The amount remains outstanding and is not written off in your accounting records.';

export enum FollowUpStatus {
    Today = 'Today',
    Upcoming = 'Upcoming',
    Pending = 'Pending',
    Overdue = 'Overdue',
    Completed = 'Completed',
}

/** Another person to reach on the account, entered in the product (see `contacts.ts`). */
export type AdditionalContact = ContactPerson;

export interface Customer {
    id: string;

    /**
     * How a row in this company's own sheet is recognised again next sync.
     * Filled from the column the company nominated when it mapped its columns.
     * Absent when the company has no stable identifier and accepted the risk.
     */
    sourceKey?: string;

    company: string;
    contactPerson: string;
    contactNumber: string;
    contactPost?: string;
    additionalContacts?: AdditionalContact[];
    email?: string;
    city?: string;
    state?: string;
    address?: string;
    taxId?: string;
    secondaryTaxId?: string;
    creditLimit?: number;
    paymentTermsDays?: number;
    paymentRank?: PaymentRank;
    /** What kind of business this is. The list is per company, not per product. */
    category?: string;

    total: number;
    /** 'Dr' = they owe us. 'Cr' = we are holding their money. */
    totalType?: BalanceType;
    ageing: AgeingAmounts;
    ageingTypes?: AgeingTypes;
    /** The roll-ups the source supplies in their own columns, when it does. */
    over90?: number;
    over90Type?: BalanceType;
    dueOver45?: number;
    dueOver45Type?: BalanceType;

    ownerMemberId?: MemberId;
    collectorMemberId?: MemberId;
    /** Whatever the sheet's owner column said, kept so a code matching nobody is visible. */
    importedOwnerCode?: string;

    followUpDate?: Date;
    /** Expected collection, for the cash-flow forecast. */
    forecastAmount?: number;
    forecastDate?: Date | string;
    /**
     * Only ever read for `Completed` — whether a person declared the money
     * collected. Today / Upcoming / Overdue / Pending are read from the date
     * every time, never stored.
     */
    status: FollowUpStatus;
    notes: string[];
    isUrgent?: boolean;
    isNewCustomer?: boolean;

    /**
     * When this account last went from owing something to owing nothing.
     *
     * Without it a customer who cleared this morning is indistinguishable from
     * one who cleared two years ago, and the settled list — usually four rows
     * in five — is unreadable. Cleared again the moment a balance comes back.
     */
    settledAt?: string;
    createdAt: Date;
    lastFollowUpOn?: Date;
    /**
     * How the latest answered promise turned out, when somebody answered it.
     * Recorded by a person answering the promise (migration 0033), never
     * inferred from the balance; cleared when a new promise is made.
     */
    promiseOutcome?: 'kept' | 'not_kept';
    /** When that answer was recorded (ISO timestamp). */
    promiseOutcomeOn?: string;
}

/**
 * Whether an account belongs in a collections count.
 *
 * A customer master carries the whole customer list, including accounts that
 * owe nothing; importing it adds them to the book with a zero balance. They are
 * real customers and belong in search and in their ledger, but counting them as
 * things to chase overstates every collector's workload.
 */
export const hasOutstanding = (item: Pick<Customer, 'total'>): boolean =>
    Math.abs(Number(item.total) || 0) > 0;

/**
 * Which half of the book a screen is showing.
 *
 * Most customers owe nothing. They are real customers — the ledger, the
 * contacts and every word of the history still matter, and a settled account is
 * the point of the whole exercise — but they are not work, and mixed into a
 * worklist they bury the few hundred accounts that are. "No follow-up set" read
 * in the thousands when the true answer was a few hundred, because the rest had
 * nothing to follow up.
 *
 * So every list about work defaults to `withDues`, and `settled` is a place to
 * go rather than a thing to wade through. Nothing is hidden and nothing is
 * dropped: settling an account only zeroes its money.
 */
export type SettlementFilter = 'withDues' | 'owes' | 'credit' | 'settled' | 'all';

/**
 * `owes` and `credit` split `withDues` by direction (0033): the accounts that
 * owe us, and the ones where we are holding the customer's money.
 */
export const SETTLEMENT_LABELS: Record<SettlementFilter, string> = {
    withDues: 'With dues',
    owes: 'Owes us',
    credit: 'In credit',
    settled: 'Settled',
    all: 'All',
};

export const matchesSettlement = (
    item: Pick<Customer, 'total' | 'totalType'>,
    filter: SettlementFilter,
): boolean => {
    switch (filter) {
        case 'all': return true;
        case 'settled': return !hasOutstanding(item);
        case 'owes': return hasOutstanding(item) && item.totalType !== 'Cr';
        case 'credit': return hasOutstanding(item) && item.totalType === 'Cr';
        default: return hasOutstanding(item);
    }
};

/**
 * One company name, written one way.
 *
 * "ACME TRADERS", "ACME  TRADERS" and "ACME TRADERS," are one firm. An import
 * that matched on the trimmed lowercase name let all three through as separate
 * customers when a customer master was loaded alongside an invoice sheet, so
 * the book carried shadow accounts at zero balance, each splitting one
 * customer's contact details and history from their money.
 *
 * Punctuation and spacing carry no meaning in a company name here, so they are
 * dropped before anything is compared. This is a *fallback* identity: a company
 * that has a stable code in its sheet should use it instead (see 07).
 */
export const companyKey = (name?: string | null): string =>
    (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

/** One code, whatever case and spacing it was typed in. */
export const codeKey = (value?: string | null): string => (value || '').trim().toUpperCase();
